import { cleanLegMix, legMixTotal, MAX_MIX_LEGS } from '../prepackLegMix'
import { ANY_PERIOD, isGameMarket, pairKey, savedPairs } from './marketPeriods'

export function supportsLegMix(definition, descriptor = null) {
  const betType = definition?.bet_type || descriptor?.bet_type
  return descriptor?.kind !== 'sports' && ['same_game_parlay', 'parlay'].includes(definition?.strategy)
    && (!betType || ['sgp', 'parlay'].includes(betType))
}

export const isPlayerMarket = (option) => Boolean(option) && (!isGameMarket(option) || option.filter_profile?.participants === 'players')

// Markets whose legs each back one team, which a player row's team link
// (same_team_as) names: a moneyline, a spread or a team total (the server's
// teamMarketKey), read from the market's filter profile, else from the
// family whose profile it takes.
const TEAM_MARKET_KINDS = new Set(['game_winner', 'spread', 'team_total'])
const TEAM_MARKET_FAMILIES = new Set(['team_moneyline', 'team_spread', 'team_total'])
export const isTeamMarket = (option) => (option?.filter_profile
  ? TEAM_MARKET_KINDS.has(option.filter_profile.kind)
  : TEAM_MARKET_FAMILIES.has(option?.market_family))

// A row stored without a period stands for its market's only pick, as the
// server reads it; with several picks it matches none until one is chosen.
function withPickPeriods(mix, pairs) {
  const periods = new Map()
  for (const pair of pairs) periods.set(pair.key, [...(periods.get(pair.key) || []), pair.period])
  return cleanLegMix(mix).map((row) => {
    const picks = periods.get(row.market_key) || []
    return !row.period && picks.length === 1 ? { ...row, period: picks[0] } : row
  })
}

/** Keep the mix in step with a changed selection; new picks start with one leg. */
export function mixForPairs(mix, pairs) {
  const previous = new Map(withPickPeriods(mix, pairs).map((row) => [pairKey(row.market_key, row.period), row]))
  return cleanLegMix(pairs.map((pair) => previous.get(pairKey(pair.key, pair.period)) || {
    market_key: pair.key, period: pair.period, count: 1,
  }))
}

export function withLegMix(definition, mix) {
  const rows = cleanLegMix(mix)
  const { leg_mix: _previous, ...filters } = definition.filters || {}
  return {
    ...definition,
    ...(rows.length ? { leg_counts: [legMixTotal(rows)] } : {}),
    filters: { ...filters, ...(rows.length ? { leg_mix: rows } : {}) },
  }
}

/** A changed market selection also changes its exact mix, when one is on. */
export function withMarketPicks(definition, patch, catalogOptions = []) {
  const next = { ...definition, filters: { ...definition.filters, ...patch } }
  if (!cleanLegMix(definition.filters?.leg_mix).length) return next
  const mix = (next.filters.market_families || []).length ? [] : mixForPairs(definition.filters.leg_mix, savedPairs(next.filters, catalogOptions))
  return withLegMix(next, mix)
}

export function initialLegMix(definition, catalogOptions = []) {
  const pairs = savedPairs(definition.filters, catalogOptions)
  const mix = mixForPairs([], pairs)
  if (mix.length === 1) {
    mix[0].count = Math.max(2, Math.min(MAX_MIX_LEGS, Number(definition.leg_counts?.[0]) || 2))
  }
  return mix
}

/** One period's capacity, shared with the existing SGP capacity check. */
export function sgpMarketCapacity(option) {
  const type = option?.participant_type ?? option?.participantType
  if (type === 'player' || option?.filter_profile?.participants === 'players') return MAX_MIX_LEGS
  return type === 'team' ? 2 : 1
}

export function legMixProblems(definition, descriptor, filterOptions) {
  const filters = definition.filters || {}
  const value = filters.leg_mix
  if (value == null || (Array.isArray(value) && value.length === 0)) return {}
  const problem = (message) => ({ leg_mix: message })
  if (!supportsLegMix(definition, descriptor)) return problem('Legs per market needs SGP or Parlay')
  if (!Array.isArray(value) || value.some((row) => !row?.market_key || !Number.isInteger(row.count) || row.count < 1)) {
    return problem('Give each market at least 1 leg')
  }
  if (!filters.market_keys?.length || filters.market_families?.length) return problem('Choose specific markets without market families')
  const pairs = savedPairs(filters, filterOptions?.market_keys)
  const mix = withPickPeriods(value, pairs)
  const ids = new Set(pairs.map((pair) => pairKey(pair.key, pair.period)))
  if (mix.length !== value.length || mix.length !== pairs.length || mix.some((row) => !row.period || !ids.has(pairKey(row.market_key, row.period)))) {
    return problem('Set legs for every selected market and period')
  }
  const total = legMixTotal(mix)
  const errors = {}
  if (total < 2 || total > MAX_MIX_LEGS) {
    errors.leg_mix = `Choose 2–${MAX_MIX_LEGS} legs in total`
    errors.leg_counts = errors.leg_mix
  } else if (definition.leg_counts?.length !== 1 || definition.leg_counts[0] !== total) {
    errors.leg_counts = `Set the leg count to ${total}`
  }
  const catalog = new Map((filterOptions?.market_keys || []).map((option) => [option.value, option]))
  const sgp = definition.strategy === 'same_game_parlay'
  for (const row of mix) {
    const option = catalog.get(row.market_key)
    if (sgp && option && !isPlayerMarket(option)) {
      const periods = row.period === ANY_PERIOD ? Math.max(1, option.periods?.length || 0) : 1
      if (row.count > sgpMarketCapacity(option) * periods) errors.leg_mix = `Too many legs of ${option.label || row.market_key} in one game`
    }
  }
  // Check the original links too: cleaning removes dangling and self links.
  for (const row of value) {
    const key = String(row.market_key).trim().toLowerCase()
    if (row.same_player_as) {
      const link = String(row.same_player_as).trim().toLowerCase()
      if (!sgp || key === link || !mix.some((other) => other.market_key === link)
        || !isPlayerMarket(catalog.get(key)) || !isPlayerMarket(catalog.get(link))) {
        errors.leg_mix = 'Link two different player markets in an SGP'
      }
    }
    if (row.same_team_as) {
      const link = String(row.same_team_as).trim().toLowerCase()
      if (!sgp || !mix.some((other) => other.market_key === link)
        || !isPlayerMarket(catalog.get(key)) || !isTeamMarket(catalog.get(link))) {
        errors.leg_mix = 'Link a player market to a moneyline, spread or team total in an SGP'
      }
    }
  }
  return errors
}
